import {
  Component,
  OnInit,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  inject,
  effect,
  ChangeDetectionStrategy,
} from '@angular/core'
import { Checkout, Depot, Producer, Supplier } from 'depoto-core/src/entities'
import { ModalContainerComponent } from '../../_common/modal-container/modal-container.component'
import { CoreService } from '../../../services'
import { StorageKeyService } from '../../../services/storage-key.service'
import { CheckoutService } from '../../../services/entity-services/checkout.service'
import { injectQuery } from '@tanstack/angular-query-experimental'
import { DepotService } from '../../../services/entity-services/depot.service'
import { ProducerService } from '../../../services/entity-services/producer.service'
import { SupplierService } from '../../../services/entity-services/supplier.services'
import { TranslateService } from '@ngx-translate/core'

@Component({
  selector: 'modal-print-export',
  templateUrl: 'modal-print-export.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ModalPrintExportComponent implements OnInit {
  private checkoutService = inject(CheckoutService)
  private depotService = inject(DepotService)
  private producerService = inject(ProducerService)
  private supplierService = inject(SupplierService)

  @ViewChild('modal') public childModal: ModalContainerComponent
  @Input() exportType: string
  @Input() dataCyAttribute: string
  @Output() resultExport: EventEmitter<any> = new EventEmitter()

  exportTypes: any = {
    purchaseExports: 'purchase-exports',
    saleExports: 'sale-exports',
    stockExports: 'stock-exports',
    inventoryExports: 'inventory-exports',
    orderExports: 'order-exports',
  }
  orderStatusTypes: string[] = ['all', 'bill', 'reservation']
  exportFormatTypes: string[] = ['pdf', 'excel']
  stockExportDateTypes: string[] = ['now', 'date']
  selectedExportFormat = 'pdf'
  stockExportDateType = 'now' // `now` | `date`
  dateFrom = ''
  dateTo = ''
  selectedDepots: Depot[] = []
  selectedCheckouts: Checkout[] = []
  selectedProducers: Producer[] = []
  selectedSuppliers: Supplier[] = []
  selectedOrderStatus = 'all'
  loading = false
  isComponentLoaded = false

  readonly queryDepotsOptions = injectQuery(() => ({
    queryKey: ['depots', 'options'],
    queryFn: async () => {
      if (this.core.services.user.hasRole('ROLE_SALESMAN') && !this.core.services.user.user?.depots?.length) {
        // TODO CORE USER
        return []
      } else {
        return await this.depotService
          .getDepots(
            {
              sort: 'name',
              direction: 'asc',
            },
            { id: null, name: null, enabled: null }
          )
          .then(result => result.items)
      }
    },
    select: data => {
      if (
        !this.depotService.doesUserHaveAllDepots(data as any[]) &&
        this.core.services.user.user?.depots?.length // TODO CORE USER
      ) {
        return this.core.services.user.user.depots // TODO CORE USER
      } else {
        return data as any[]
      }
    },
  }))

  readonly queryCheckouts = injectQuery(() => ({
    queryKey: ['checkouts', 'options'],
    queryFn: async () => {
      return await this.checkoutService
        .getCheckouts({ page: 1, sort: 'name', direction: 'asc' })
        .then(result => result.items)
    },
    select: data => {
      if (this.core.services.user.hasRole('ROLE_SALESMAN') && !this.core.services.user.user?.checkouts?.length) {
        // TODO CORE USER
        return []
      } else if (
        !this.checkoutService.doesUserHaveAllCheckouts(data as any[]) &&
        this.core.services.user.user?.checkouts?.length // TODO CORE USER
      ) {
        return this.core.services.user.user.checkouts // TODO CORE USER
      } else {
        return data as any
      }
    },
  }))

  readonly queryProducers = injectQuery(() => ({
    queryKey: ['producers'],
    queryFn: async () => {
      return await this.producerService.getProducers({}, { id: null, name: null }).then(result => {
        return result.items
      })
    },
  }))

  readonly querySuppliers = injectQuery(() => ({
    queryKey: ['suppliers'],
    queryFn: async () => {
      return await this.supplierService
        .getSuppliers(
          {
            sort: 'name',
            direction: 'asc',
          },
          { id: null, name: null }
        )
        .then(result => {
          return result.items
        })
    },
  }))

  constructor(
    private core: CoreService,
    private keyService: StorageKeyService,
    private translateService: TranslateService
  ) {
    effect(() => {
      this.queryProducers.data()
      this.queryCheckouts.data()
      this.querySuppliers.data()
      this.queryDepotsOptions.data()
    })
  }

  async ngOnInit() {
    await this.refreshFiltersData()
  }

  showChildModal(): void {
    this.selectedCheckouts = []
    this.selectedDepots = []
    this.selectedSuppliers = []
    this.selectedProducers = []
    this.selectedOrderStatus = 'all'
    this.selectedExportFormat = 'pdf'
    this.stockExportDateType = 'now'
    this.childModal.showChildModal()
  }

  hideChildModal(): void {
    this.childModal.hideChildModal()
  }

  loadViewSettings(): void {
    const settings = this.core.services.storage.getSync(this.keyService.getStorageKeyForEntity('modalPrintExports'))
    if (settings && Object.keys(settings).length > 0) {
      this.selectedDepots = settings.selectedDepots || []
      this.selectedCheckouts = settings.selectedCheckouts || []
      this.selectedProducers = settings.selectedProducers || []
      this.selectedSuppliers = settings.selectedSuppliers || []
      this.dateFrom = settings.dateFrom
      this.dateTo = settings.dateTo
    }
  }

  saveViewSettings(): void {
    const settings = {
      selectedDepots: this.selectedDepots,
      selectedCheckouts: this.selectedCheckouts,
      selectedProducers: this.selectedProducers,
      selectedSuppliers: this.selectedSuppliers,
      dateFrom: this.dateFrom,
      dateTo: this.dateTo,
    }
    this.core.services.storage.set(this.keyService.getStorageKeyForEntity('modalPrintExports'), settings)
  }

  async refreshFiltersData() {
    try {
      this.loading = true
      this.loadViewSettings()
      this.isComponentLoaded = true
      this.loading = false
    } catch (e) {
      console.warn('modal-print-exports::refreshData error', e)
    }
  }

  createPrintExport() {
    if (
      (this.exportType === this.exportTypes.stockExports ||
        this.exportType === this.exportTypes.inventoryExports ||
        this.exportType === this.exportTypes.purchaseExports) &&
      this.selectedDepots.length < 1
    ) {
      return alert(this.translateService.instant('modal-print-export.select-warehouse'))
    }
    const printExport: any = {}
    const selectedDepots = this.queryDepotsOptions
      .data()
      ?.filter(d => this.selectedDepots.some(depot => Number(depot) === Number(d.id)))
    const selectedCheckouts = this.queryCheckouts
      .data()
      ?.filter(ch => this.selectedCheckouts.some(checkout => Number(checkout) === Number(ch.id)))
    const selectedSuppliers = this.querySuppliers
      .data()
      ?.filter(s => this.selectedSuppliers.some(supplier => Number(supplier) === Number(s.id)))
    const selectedProducers = this.queryProducers
      .data()
      ?.filter(p => this.selectedProducers.some(producer => Number(producer) === Number(p.id)))
    switch (this.exportType) {
      case this.exportTypes.purchaseExports:
        printExport.dateFrom = this.dateFrom
        printExport.dateTo = this.dateTo
        printExport.depots = [...selectedDepots]
        printExport.checkouts = [...selectedCheckouts]
        printExport.suppliers = [...selectedSuppliers]
        printExport.producers = [...selectedProducers]
        break
      case this.exportTypes.saleExports:
        printExport.dateFrom = this.dateFrom
        printExport.dateTo = this.dateTo
        printExport.checkouts = [...selectedCheckouts]
        printExport.suppliers = [...selectedSuppliers]
        printExport.producers = [...selectedProducers]
        break
      case this.exportTypes.stockExports:
        if (this.stockExportDateType === 'date' && this.dateFrom && this.dateFrom.length > 0) {
          printExport.date = this.dateFrom
        }
        printExport.depots = [...selectedDepots]
        printExport.suppliers = [...selectedSuppliers]
        printExport.producers = [...selectedProducers]
        break
      case this.exportTypes.inventoryExports:
        if (this.dateFrom && this.dateFrom.length > 0) {
          printExport.date = this.dateFrom
        }
        printExport.depots = [...selectedDepots]
        printExport.suppliers = [...selectedSuppliers]
        printExport.producers = [...selectedProducers]
        break
      case this.exportTypes.orderExports:
        if (this.selectedOrderStatus !== 'all') {
          printExport.status = this.selectedOrderStatus
        }
        printExport.dateFrom = this.dateFrom
        printExport.dateTo = this.dateTo
        printExport.checkouts = [...selectedCheckouts]
        break
    }
    if (this.exportType !== this.exportTypes.inventoryExports) {
      printExport.format = this.selectedExportFormat
    }
    this.resultExport.emit(printExport)
    this.hideChildModal()
  }
}
